import React, { useState, useEffect, useMemo } from "react";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  IconButton,
  TablePagination,
  Alert,
  Snackbar,
  Tooltip,
  TableFooter,
  Menu,
  MenuItem,
  ButtonGroup,
  CircularProgress
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, ArrowDropDown as ArrowDropDownIcon, Visibility as ViewIcon } from "@mui/icons-material";
import { Modal, Grid } from '@mui/material';
import { collection, addDoc, onSnapshot, query, orderBy, doc, updateDoc, deleteDoc, serverTimestamp, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { firestore } from '../../../firebase.config';
import { useAuth } from '../../../AuthContext';
import { useUserContext } from '../../../components/context/UserContext';
import BulkImportModal from './BulkImportModal';

const TransactionManager = () => {
  const { currentUser } = useAuth();
  const { userProfile } = useUserContext();
  const tenantId = userProfile?.tenantId;
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    description: "",
    type: "Credit",
    amount: "",
    branchName: "",
    accountName: "",
    accountNumber: "",
    accountType: '',
    borrowerName: '',
    loanAmount: '',
    loanStartDate: '',
    loanEndDate: '',
    emiAmount: '',
    outstandingAmount: '',
    loanType: ''
  });

  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // single combined filter dropdown (values: 'all' | 'branch:<name>' | 'account:<number>')
  const [anchorEl, setAnchorEl] = useState({
    branch: null,
    accountType: null
  });
  const [filters, setFilters] = useState({
    branch: 'all',
    accountType: 'all',
    startDate: '',
    endDate: ''
  });
  const [dateError, setDateError] = useState('');
  const [viewingTransaction, setViewingTransaction] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [importOpen, setImportOpen] = useState(false);

  // Helper to show snackbar (declared before effects so it can be used anywhere)
  function showSnackbar(message, severity = "success") {
    setSnackbar({ open: true, message, severity });
  }

  useEffect(() => {
    if (!tenantId) {
      showSnackbar("Please wait while loading user profile...", "info");
      return;
    }

    // Load cached transactions from localStorage (so page refresh shows data quickly/offline)
    try {
      const key = `transactions_${tenantId}`;
      const cached = localStorage.getItem(key);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setTransactions(parsed);
        }
      }
    } catch (e) {
      console.warn('Failed to read transactions from localStorage', e);
    }

    const q = query(
      collection(firestore, 'Accounting Transactions Master'),
      where('tenantId', '==', tenantId)
    );

    setIsLoading(true);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      try {
        const items = snapshot.docs.map(docSnap => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            // normalize createdAt to millis for sorting
            createdAt: data.createdAt?.toMillis?.() || Date.now()
          };
        });

        // Sort items by createdAt in descending order
        const sortedItems = items.sort((a, b) => b.createdAt - a.createdAt);
        setTransactions(sortedItems);

        // Persist a local copy keyed by tenant so refresh keeps state if Firestore delayed
        try {
          const key = `transactions_${tenantId}`;
          localStorage.setItem(key, JSON.stringify(sortedItems));
        } catch (e) {
          console.warn('Failed to write transactions to localStorage', e);
        }

      } catch (err) {
        console.error('Error processing transaction snapshot:', err);
        showSnackbar('Error loading transactions', 'error');
      } finally {
        setIsLoading(false);
      }
    }, (err) => {
      console.error('Error listening to transactions:', err);
      showSnackbar(err.message || 'Error listening to transactions', 'error');
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [tenantId]); // <-- Add tenantId as dependency

  // Whenever transactions change, persist to localStorage (tenant-specific)
  useEffect(() => {
    if (!tenantId) return;
    try {
      const key = `transactions_${tenantId}`;
      localStorage.setItem(key, JSON.stringify(transactions));
    } catch (e) {
      // ignore write failures
    }
  }, [transactions, tenantId]);

  const handleChange = (e) => {
    setNewEntry({ ...newEntry, [e.target.name]: e.target.value });
  };

  const handleAdd = () => {
    // Use tenantId from userProfile
    if (!tenantId) {
      showSnackbar("Session expired or tenant not found. Please log in again.", "error");
      return;
    }
    if (!newEntry.date || !newEntry.description || !newEntry.amount) {
      showSnackbar("Please fill all required fields", "error");
      return;
    }

    if (isNaN(parseFloat(newEntry.amount)) || parseFloat(newEntry.amount) <= 0) {
      showSnackbar("Please enter a valid amount", "error");
      return;
    }

    (async () => {
      try {
        const amountValue = parseFloat(newEntry.amount).toFixed(2);

        if (editingId !== null) {
          // Update existing transaction
          const tx = transactions[editingId];
          if (!tx || !tx.id) {
            showSnackbar('Unable to find transaction to update', 'error');
            return;
          }
          const txRef = doc(firestore, 'Accounting Transactions Master', tx.id);
          await updateDoc(txRef, {
            date: newEntry.date,
            description: newEntry.description,
            type: newEntry.type,
            amount: amountValue,
            accountType: newEntry.accountType || '',
            borrowerName: newEntry.borrowerName || '',
            loanAmount: newEntry.loanAmount || '',
            loanStartDate: newEntry.loanStartDate || '',
            loanEndDate: newEntry.loanEndDate || '',
            emiAmount: newEntry.emiAmount || '',
            outstandingAmount: newEntry.outstandingAmount || '',
            loanType: newEntry.loanType || '',
            branchName: newEntry.branchName || '',
            accountName: newEntry.accountName || '',
            accountNumber: newEntry.accountNumber || '',
            updatedBy: currentUser?.uid || currentUser?.email || '',
            updatedAt: serverTimestamp(),
            tenantId: tenantId
          });

          // Optimistically update local state so UI updates immediately
          setTransactions(prev =>
            prev.map(t =>
              t.id === tx.id
                ? {
                    ...t,
                    date: newEntry.date,
                    description: newEntry.description,
                    type: newEntry.type,
                    amount: amountValue,
                    accountType: newEntry.accountType || '',
                    borrowerName: newEntry.borrowerName || '',
                    loanAmount: newEntry.loanAmount || '',
                    loanStartDate: newEntry.loanStartDate || '',
                    loanEndDate: newEntry.loanEndDate || '',
                    emiAmount: newEntry.emiAmount || '',
                    outstandingAmount: newEntry.outstandingAmount || '',
                    loanType: newEntry.loanType || '',
                    branchName: newEntry.branchName || '',
                    accountName: newEntry.accountName || '',
                    accountNumber: newEntry.accountNumber || '',
                    updatedAt: Date.now()
                  }
                : t
            )
          );

          setEditingId(null);
          showSnackbar('Transaction updated successfully');
        } else {
          // Create new transaction
          const docRef = await addDoc(collection(firestore, 'Accounting Transactions Master'), {
            date: newEntry.date,
            description: newEntry.description,
            type: newEntry.type,
            amount: amountValue,
            branchName: newEntry.branchName || '',
            accountName: newEntry.accountName || '',
            accountNumber: newEntry.accountNumber || '',
            accountType: newEntry.accountType || '',
            borrowerName: newEntry.borrowerName || '',
            loanAmount: newEntry.loanAmount || '',
            loanStartDate: newEntry.loanStartDate || '',
            loanEndDate: newEntry.loanEndDate || '',
            emiAmount: newEntry.emiAmount || '',
            outstandingAmount: newEntry.outstandingAmount || '',
            loanType: newEntry.loanType || '',
            createdBy: currentUser?.uid || currentUser?.email || '',
            createdAt: serverTimestamp(),
            tenantId: tenantId
          });

          // Optimistic local entry so table + total update immediately
          const optimisticTx = {
            id: docRef.id,
            date: newEntry.date,
            description: newEntry.description,
            type: newEntry.type,
            amount: amountValue,
            branchName: newEntry.branchName || '',
            accountName: newEntry.accountName || '',
            accountNumber: newEntry.accountNumber || '',
            accountType: newEntry.accountType || '',
            borrowerName: newEntry.borrowerName || '',
            loanAmount: newEntry.loanAmount || '',
            loanStartDate: newEntry.loanStartDate || '',
            loanEndDate: newEntry.loanEndDate || '',
            emiAmount: newEntry.emiAmount || '',
            outstandingAmount: newEntry.outstandingAmount || '',
            loanType: newEntry.loanType || '',
            createdBy: currentUser?.uid || currentUser?.email || '',
            createdAt: Date.now(), // local timestamp for immediate ordering
            tenantId: tenantId
          };
          setTransactions(prev => [optimisticTx, ...prev]);

          showSnackbar('Transaction added successfully');
        }
      } catch (error) {
        console.error('Error saving transaction:', error);
        showSnackbar('Failed to save transaction', 'error');
      }
    })();

    // Reset form
    setNewEntry({
      date: new Date().toISOString().split('T')[0],
      description: "",
      type: "Credit",
      amount: "",
      branchName: "",
      accountName: "",
      accountNumber: ""
    });
  };

  const handleEdit = (rowIndex) => {
    const transaction = transactions[rowIndex];
    if (!transaction) return;
    setNewEntry({
      date: transaction.date,
      description: transaction.description,
      type: transaction.type,
      amount: transaction.amount,
      branchName: transaction.branchName || '',
      accountName: transaction.accountName || '',
      accountNumber: transaction.accountNumber || '',
      accountType: transaction.accountType || '',
      borrowerName: transaction.borrowerName || '',
      loanAmount: transaction.loanAmount || '',
      loanStartDate: transaction.loanStartDate || '',
      loanEndDate: transaction.loanEndDate || '',
      emiAmount: transaction.emiAmount || '',
      outstandingAmount: transaction.outstandingAmount || '',
      loanType: transaction.loanType || ''
    });
    setEditingId(rowIndex);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (rowIndex) => {
    const tx = transactions[rowIndex];
    if (!tx || !tx.id) {
      showSnackbar('Unable to find transaction to delete', 'error');
      return;
    }
    (async () => {
      // Optimistically remove from local state so UI and portfolio update immediately
      const prev = transactions;
      setTransactions(prevState => prevState.filter(t => t.id !== tx.id));

      try {
        await deleteDoc(doc(firestore, 'Accounting Transactions Master', tx.id));
        showSnackbar('Transaction deleted successfully');
      } catch (error) {
        console.error('Error deleting transaction:', error);
        // Revert local state on failure
        setTransactions(prev);
        showSnackbar('Failed to delete transaction', 'error');
      }
    })();
  };

  const formatAmount = (amount) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `₹ ${num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDetails = (transaction) => {
    if (!transaction?.accountType) return null;

    const details = [];
    const type = transaction.accountType;

    if (type === 'Saving') {
      const minBal = parseFloat(transaction.minimumBalance);
      const intRate = parseFloat(transaction.interestRate);
      if (minBal > 0) details.push(`Min Bal: ${formatAmount(minBal)}`);
      if (intRate > 0) details.push(`Int: ${intRate.toFixed(2)}%`);
    }
    else if (type === 'Current') {
      const odLimit = parseFloat(transaction.overdraftLimit);
      if (odLimit > 0) details.push(`OD Limit: ${formatAmount(odLimit)}`);
    }
    else if (type === 'Deposit') {
      const duration = parseInt(transaction.duration);
      const intRate = parseFloat(transaction.interestRate);
      if (duration > 0) details.push(`Duration: ${duration} months`);
      if (intRate > 0) details.push(`Int: ${intRate.toFixed(2)}%`);
    }
    else if (type === 'Loan') {
      if (transaction.loanType) details.push(`Type: ${transaction.loanType}`);
      const intRate = parseFloat(transaction.interestRate);
      if (intRate > 0) details.push(`Int: ${intRate.toFixed(2)}%`);
      if (transaction.emiStartDate) {
        const emiDate = new Date(transaction.emiStartDate).toLocaleDateString('en-IN');
        details.push(`EMI Start: ${emiDate}`);
      }
    }

    return details.length > 0 ? details.join(', ') : null;
  };

  const getDetailsTooltip = (transaction) => {
    if (!transaction?.accountType) return '';

    const type = transaction.accountType;
    const details = [];

    if (type === 'Saving' || type === 'Deposit') {
      details.push(`Account Holder: ${transaction.accountHolderName || 'N/A'}`);
      if (transaction.ifscCode) details.push(`IFSC: ${transaction.ifscCode}`);
    }
    else if (type === 'Current') {
      details.push(`Business: ${transaction.businessName || 'N/A'}`);
      if (transaction.branchCode) details.push(`Branch Code: ${transaction.branchCode}`);
      if (transaction.accountManager) details.push(`Manager: ${transaction.accountManager}`);
    }
    else if (type === 'Loan') {
      details.push(`Borrower: ${transaction.borrowerName || 'N/A'}`);
      if (transaction.loanAmount) details.push(`Loan Amount: ${formatAmount(transaction.loanAmount)}`);
    }

    const displayDetails = formatDetails(transaction);
    if (displayDetails) details.push(displayDetails);

    return details.join('\n');
  };

  // Derive portfolio summary values from transactions according to rules:
  // - Deposit: contributes to Total Deposit and Current Balance
  // - Saving | Current: affect Current Balance by credit/debit
  // - Loan: outstanding increases for disbursement (debit), decreases for repayment (credit)
  // Current Balance is overall signed sum of transactions (credit positive, debit negative)
  const portfolio = useMemo(() => {
    let currentBalance = 0;
    let totalDeposit = 0;
    let outstandingLoan = 0;

    transactions.forEach(t => {
      const amount = parseFloat(t.amount) || 0;
      const signed = t.type === 'Credit' ? amount : -amount; // credit +, debit -

      const acctType = (t.accountType || '').toString();

      // Deposit: add signed amount to totalDeposit (credit increases, debit decreases)
      if (acctType === 'Deposit') {
        totalDeposit += signed;
      }

      // Loan: outstanding changes opposite to signed value
      // (disbursement is a debit -> signed negative -> -signed positive -> increases outstanding)
      if (acctType === 'Loan') {
        outstandingLoan += -signed;
      }

      // Current balance: include all transactions (this keeps previous behaviour)
      currentBalance += signed;
    });

    return {
      currentBalance,
      totalDeposit,
      outstandingLoan
    };
  }, [transactions]);

  // Helper to parse transaction date safely
  const parseTxDate = (d) => {
    if (!d) return null;
    // If it's a Firestore timestamp-like object
    if (typeof d === 'object' && d.toDate) return d.toDate();
    // If it's a numeric millis
    if (typeof d === 'number') return new Date(d);
    // Otherwise assume string
    return new Date(d);
  };

  // Compute opening balance (before selectedDate) and closing (end of selectedDate)
  const computeBalancesForDate = (dateStr) => {
    if (!dateStr) return { opening: 0, closing: 0 };
    const start = new Date(`${dateStr}T00:00:00`);
    const end = new Date(`${dateStr}T23:59:59.999`);

    let opening = 0;
    let closing = 0;

    transactions.forEach(tx => {
      const txDate = parseTxDate(tx.date);
      if (!txDate || isNaN(txDate.getTime())) return;
      const amount = parseFloat(tx.amount) || 0;
      const signed = tx.type === 'Credit' ? amount : -amount;

      if (txDate < start) {
        opening += signed;
      }

      if (txDate <= end) {
        closing += signed;
      }
    });

    return { opening, closing };
  };

  const { opening: openingBalance, closing: closingBalance } = computeBalancesForDate(selectedDate);

  // Handle menu open/close
  const handleMenuClick = (filterType) => (event) => {
    setAnchorEl(prev => ({
      ...prev,
      [filterType]: event.currentTarget
    }));
  };

  const handleMenuClose = (filterType) => () => {
    setAnchorEl(prev => ({
      ...prev,
      [filterType]: null
    }));
  };

  // Handle filter selection
  const handleFilterSelect = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    handleMenuClose(filterType)();
    setPage(0);
  };

  const handleDateChange = (type, value) => {
    const newFilters = {
      ...filters,
      [type]: value
    };

    // Check if both dates are selected
    if (newFilters.startDate && newFilters.endDate) {
      const start = new Date(newFilters.startDate);
      const end = new Date(newFilters.endDate);

      // Calculate difference in days
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays > 365) {
        setDateError('Date range cannot exceed 1 year');
        return;
      } else {
        setDateError('');
      }
    } else {
      setDateError('');
    }

    setFilters(newFilters);
  };

  // Apply filters to transactions
  let transactionsToDisplay = transactions.slice();

  // Apply branch filter
  if (filters.branch !== 'all') {
    transactionsToDisplay = transactionsToDisplay.filter(tx =>
      tx.branchName === filters.branch
    );
  }

  // Apply account type filter
  if (filters.accountType !== 'all') {
    transactionsToDisplay = transactionsToDisplay.filter(tx =>
      tx.accountType === filters.accountType
    );
  }

  // Apply date range filter
  if (filters.startDate || filters.endDate) {
    transactionsToDisplay = transactionsToDisplay.filter(tx => {
      const txDate = new Date(tx.date);
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;

      if (startDate && endDate) {
        return txDate >= new Date(startDate.setHours(0, 0, 0, 0)) &&
          txDate <= new Date(endDate.setHours(23, 59, 59, 999));
      } else if (startDate) {
        return txDate >= new Date(startDate.setHours(0, 0, 0, 0));
      } else if (endDate) {
        return txDate <= new Date(endDate.setHours(23, 59, 59, 999));
      }
      return true;
    });
  }

  // default sorting: newest first (createdAt desc)
  transactionsToDisplay = transactionsToDisplay.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setHasMore(true);
  };

  // Calculate empty rows for pagination based on filtered list
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - transactionsToDisplay.length) : 0;

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [filters.branch, filters.accountType, filters.startDate, filters.endDate]);

  // Get current transactions for infinite scroll
  const currentTransactions = useMemo(() => {
    // Show all transactions up to the current page (infinite scroll)
    return transactionsToDisplay.slice(0, (page + 1) * rowsPerPage);
  }, [page, rowsPerPage, transactionsToDisplay]);

  // Handle scroll event for infinite loading
  useEffect(() => {
    const handleScroll = () => {
      if (loading || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;

      // Load more when user is near the bottom (within 200px)
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        loadMoreTransactions();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, hasMore]);

  const loadMoreTransactions = () => {
    if (loading) return;

    setLoading(true);
    // Load next page immediately (no delay)
    const nextPage = page + 1;
    const start = nextPage * rowsPerPage;
    if (start >= transactionsToDisplay.length) {
      setHasMore(false);
    } else {
      setPage(nextPage);
    }
    setLoading(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleViewTransaction = (transaction) => {
    setViewingTransaction(transaction);
  };

  const handleCloseViewModal = () => {
    setViewingTransaction(null);
  };

  // derive unique branch and accountType options from transactions for the single dropdown
  const uniqueBranches = Array.from(new Set(transactions.map(t => (t.branchName || '').toString()).filter(v => v && v.trim() !== '')));
  const uniqueAccountTypes = Array.from(new Set(transactions.map(t => (t.accountType || '').toString()).filter(v => v && v.trim() !== '')));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: "bold", display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            Transaction Manager
            <Button variant="contained" size="small" onClick={() => navigate('/accounting/transactions/new')} startIcon={<AddIcon />} sx={{ ml: 2 }}>
              New
            </Button>
            <Button variant="outlined" size="small" onClick={() => setImportOpen(true)} sx={{ ml: 2 }}>Import Excel</Button>
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 240, alignItems: 'flex-end' }}>
        </Box>
      </Box>


      {/* Portfolio Summary */}
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        gap: { xs: 2, md: 0 },
        alignItems: { xs: 'stretch', md: 'flex-start' },
        mb: 2
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: { xs: 0, md: 2 } }}>
        {/* Date Range Filter */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={filters.startDate}
            onChange={(e) => handleDateChange('startDate', e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: filters.endDate || undefined
            }}
            error={!!dateError}
            helperText={dateError}
            sx={{ width: 180 }}
          />
          <Typography variant="body1">to</Typography>
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={filters.endDate}
            onChange={(e) => handleDateChange('endDate', e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: filters.startDate || undefined
            }}
            error={!!dateError}
            sx={{ width: 180 }}
          />
          <Button
            variant="outlined"
            onClick={() => {
              setFilters(prev => ({ ...prev, startDate: '', endDate: '' }));
              setDateError('');
            }}
            sx={{ ml: 1 }}
          >
            Clear Dates
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleMenuClick('branch')}
            endIcon={<ArrowDropDownIcon />}
            sx={{
              color: 'primary.main',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'transparent'
              }
            }}
          >
            {filters.branch === 'all' ? 'ALL BRANCHES' : filters.branch.toUpperCase()}
          </Button>
          <Menu
            anchorEl={anchorEl.branch}
            open={Boolean(anchorEl.branch)}
            onClose={handleMenuClose('branch')}
            PaperProps={{
              sx: {
                maxHeight: 300,
                minWidth: 200
              }
            }}
          >
            <MenuItem
              onClick={() => handleFilterSelect('branch', 'all')}
              selected={filters.branch === 'all'}
            >
              ALL BRANCHES
            </MenuItem>
            {uniqueBranches.map(branch => (
              <MenuItem
                key={branch}
                onClick={() => handleFilterSelect('branch', branch)}
                selected={filters.branch === branch}
              >
                {branch.toUpperCase()}
              </MenuItem>
            ))}
          </Menu>

          <Button
            variant="outlined"
            onClick={handleMenuClick('accountType')}
            endIcon={<ArrowDropDownIcon />}
            sx={{
              color: 'primary.main',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'transparent'
              }
            }}
          >
            {filters.accountType === 'all' ? 'ALL TYPES' : filters.accountType.toUpperCase()}
          </Button>
          <Menu
            anchorEl={anchorEl.accountType}
            open={Boolean(anchorEl.accountType)}
            onClose={handleMenuClose('accountType')}
            PaperProps={{
              sx: {
                maxHeight: 300,
                minWidth: 200
              }
            }}
          >
            <MenuItem
              onClick={() => handleFilterSelect('accountType', 'all')}
              selected={filters.accountType === 'all'}
            >
              ALL TYPES
            </MenuItem>
            {uniqueAccountTypes.map(type => (
              <MenuItem
                key={type}
                onClick={() => handleFilterSelect('accountType', type)}
                selected={filters.accountType === type}
              >
                {type.toUpperCase()}
              </MenuItem>
            ))}
          </Menu>

          <Button
            variant="outlined"
            onClick={() => {
              setFilters({
                branch: 'all',
                accountType: 'all',
                startDate: '',
                endDate: ''
              });
            }}
            sx={{
              color: 'primary.main',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'transparent'
              }
            }}
          >
            RESET ALL FILTERS
          </Button>
        </Box>
      </Box>
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2, minWidth: { xs: 'unset', md: 240 }, width: { xs: '100%', md: 300 }, mb: { xs: 2, md: 0 } }}>
        <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 1.5, fontWeight: 'medium' }}>
          Portfolio Summary
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Outstanding Loan */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Outstanding Loan
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: portfolio.outstandingLoan >= 0 ? 'success.main' : 'error.main', textAlign: 'right' }}>
              {formatAmount(Math.abs(portfolio.outstandingLoan))} {portfolio.outstandingLoan >= 0 ? 'CR' : 'DR'}
            </Typography>
          </Box>

          {/* Total Deposit */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Total Deposit
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: portfolio.totalDeposit >= 0 ? 'success.main' : 'error.main', textAlign: 'right' }}>
              {formatAmount(Math.abs(portfolio.totalDeposit))} {portfolio.totalDeposit >= 0 ? 'CR' : 'DR'}
            </Typography>
          </Box>

          {/* Current Balance (combined) */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Current Balance
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: portfolio.currentBalance >= 0 ? 'success.main' : 'error.main', textAlign: 'right' }}>
              {portfolio.currentBalance >= 0 ? '+' : '-'} {formatAmount(Math.abs(portfolio.currentBalance))} {portfolio.currentBalance >= 0 ? 'CR' : 'DR'}
            </Typography>
          </Box>
        </Box>
      </Paper>
      </Box>


      {/* Selected date and Opening/Closing balances - Commented out as per request
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField
          label="Select Date"
          type="date"
          size="small"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          sx={{ width: 180 }}
        />

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box sx={{ p: 1, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1, minWidth: 180 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Opening Balance</Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: openingBalance >= 0 ? 'success.main' : 'error.main' }}>
              {openingBalance >= 0 ? '+' : '-'} {formatAmount(Math.abs(openingBalance))} {openingBalance >= 0 ? 'CR' : 'DR'}
            </Typography>
          </Box>

          <Box sx={{ p: 1, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1, minWidth: 180 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Closing Balance</Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: closingBalance >= 0 ? 'success.main' : 'error.main' }}>
              {closingBalance >= 0 ? '+' : '-'} {formatAmount(Math.abs(closingBalance))} {closingBalance >= 0 ? 'CR' : 'DR'}
            </Typography>
          </Box>
        </Box>
      </Box>
      */}

   

      <BulkImportModal open={importOpen} onClose={() => setImportOpen(false)} tenantId={tenantId} currentUser={currentUser} onImported={() => { /* noop - Firestore snapshot will refresh list */ }} />

      {/* Transactions Table */}
      <Paper elevation={3} sx={{ mt: 2, width: '100%', borderRadius: 2, boxShadow: 3, border: '1px solid #e0e0e0', p: 2 }}>
        <TableContainer sx={{ maxHeight: { xs: 400, md: 'calc(100vh - 220px)' }, width: '100%' }}>
          <Table stickyHeader size="small" sx={{ minWidth: 600, tableLayout: 'fixed', fontSize: { xs: '0.85rem', md: '1rem' }, backgroundColor: 'background.paper' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: { xs: 90, md: '12%' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Account Type</TableCell>
                <TableCell sx={{ width: { xs: 90, md: '12%' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Transaction Date</TableCell>
                <TableCell sx={{ width: { xs: 110, md: '15%' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Account Number</TableCell>
                <TableCell sx={{ width: { xs: 120, md: '20%' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Bank and Branch</TableCell>
                <TableCell sx={{ width: { xs: 120, md: '25%' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Description</TableCell>
                <TableCell align="right" sx={{ width: { xs: 80, md: '10%' }, pr: 3, fontSize: { xs: '0.8rem', md: '1rem' } }}>Amount</TableCell>
                <TableCell align="center" sx={{ width: { xs: 60, md: '6%' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentTransactions.length > 0 ? (
                currentTransactions.map((transaction, rowIndex) => (
                  <TableRow key={`${transaction.id}-${transaction.createdAt}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', width: { xs: 90, md: '12%' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>{transaction.accountType || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', width: { xs: 90, md: '12%' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>{new Date(transaction.date).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', width: { xs: 110, md: '15%' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>{transaction.accountNumber || '—'}</TableCell>
                    <TableCell sx={{ width: { xs: 120, md: '20%' }, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: { xs: '0.8rem', md: '1rem' } }}>
                      {transaction.branchName || '—'}
                      {transaction.bankName ? ` (${transaction.bankName})` : ''}
                    </TableCell>
                    <TableCell sx={{ width: { xs: 120, md: '25%' }, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: { xs: '0.8rem', md: '1rem' } }} title={transaction.description}>
                      {transaction.description || 'N/A'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: transaction.type === "Credit" ? 'success.main' : 'error.main',
                        fontWeight: '500',
                        width: { xs: 80, md: '10%' },
                        pr: 3,
                        fontSize: { xs: '0.8rem', md: '1rem' }
                      }}
                    >
                      {transaction.type === "Credit" ? '+' : '-'} {formatAmount(transaction.amount)}
                    </TableCell>
                    <TableCell align="center" sx={{ width: { xs: 60, md: '6%' }, fontSize: { xs: '0.8rem', md: '1rem' } }}>
                      <span style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewTransaction(transaction)}
                            color="primary"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      No transactions found. Add your first transaction above.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {loading && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 2 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && !hasMore && transactionsToDisplay.length > 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No more transactions to load
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* View Transaction Modal */}
      <Modal
        open={!!viewingTransaction}
        onClose={handleCloseViewModal}
        aria-labelledby="view-transaction-title"
        aria-describedby="view-transaction-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 1,
          maxHeight: '90vh',
          overflowY: 'auto'
        }}>
          <Typography id="view-transaction-title" variant="h6" component="h2" gutterBottom>
            Transaction Details
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                <Typography>{viewingTransaction?.date ? new Date(viewingTransaction.date).toLocaleDateString() : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                <Typography>{viewingTransaction?.type || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography>{viewingTransaction?.description || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                <Typography sx={{ color: viewingTransaction?.type === 'Credit' ? 'success.main' : 'error.main' }}>
                  {viewingTransaction?.type === 'Credit' ? '+' : '-'} {formatAmount(viewingTransaction?.amount || 0)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Branch</Typography>
                <Typography>{viewingTransaction?.branchName || 'N/A'}</Typography>
              </Grid>
              {/* <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Account Name</Typography>
                <Typography>{viewingTransaction?.accountName || 'N/A'}</Typography>
              </Grid> */}
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Account Number</Typography>
                <Typography>{viewingTransaction?.accountNumber || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Created By</Typography>
                <Typography>{viewingTransaction?.createdBy || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                <Typography>{viewingTransaction?.createdAt ? new Date(viewingTransaction.createdAt).toLocaleString() : 'N/A'}</Typography>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={handleCloseViewModal} variant="outlined">
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default TransactionManager;